import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, Eyebrow } from "./Reveal";
import { Plus } from "lucide-react";

const faqs = [
  {
    q: "Will this replace my office staff?",
    a: "No — it backfills them. Backfill catches what humans can't: simultaneous calls, after-hours rings, and leads that go cold while your team is on another line. Your office still books the jobs and owns the relationship.",
  },
  {
    q: "How fast does setup take?",
    a: "Most contractors are live within 48 hours. We connect your phone line, load your branding and service list, set your qualification questions and escalation rules, then run test calls with your team before going live.",
  },
  {
    q: "What if a call is a real emergency, like a gas leak?",
    a: "Emergency keywords and situations route instantly to a real dispatcher or your on-call technician — by phone call, not text. You define exactly what counts as an emergency and who gets paged.",
  },
  {
    q: "Does it work with my CRM?",
    a: "Today, your Backfill dashboard is the system of record: every recovered call, qualified lead, booking, and full conversation transcript lives there, ready for your office. Direct CRM connections are on the roadmap — tell us which one you run and we'll prioritize it.",
  },
  {
    q: "What happens if Backfill doesn't recover revenue?",
    a: "On the Growth plan, you're covered by our revenue guarantee: if Backfill doesn't recover at least its own fee in booked jobs within 90 days, we keep working free until it does.",
  },
];

export default function FAQ() {
  const [open, setOpen] = useState(0);

  // FAQPage structured data: all five Q&As — text matches the visible page verbatim.
  // (48h setup + 90-day guarantee confirmed by the owner on 2026-10-07.)
  const faqJsonLd = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  }).replace(/</g, "\\u003c");

  return (
    <section id="faq" data-testid="faq-section" className="border-t border-white/[0.06] py-24 lg:py-32">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: faqJsonLd }} />
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <Reveal className="text-center">
          <Eyebrow>FAQ</Eyebrow>
          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
            Straight answers for busy owners
          </h2>
        </Reveal>

        <div className="mt-12 space-y-3">
          {faqs.map((f, i) => {
            const isOpen = open === i;
            return (
              <Reveal key={i} delay={i * 0.05}>
                <div
                  data-testid={`faq-item-${i}`}
                  className={`overflow-hidden rounded-lg border transition-colors duration-300 ${
                    isOpen
                      ? "border-[#F25F22]/40 bg-[#12161F]"
                      : "border-white/[0.08] bg-[#12161F]/60 hover:border-white/[0.16]"
                  }`}
                >
                  <button
                    data-testid={`faq-question-${i}`}
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
                  >
                    <span className="font-display text-base font-semibold text-slate-100">
                      {f.q}
                    </span>
                    <motion.span
                      animate={{ rotate: isOpen ? 45 : 0 }}
                      transition={{ duration: 0.25 }}
                      className="shrink-0"
                    >
                      <Plus className={`h-5 w-5 ${isOpen ? "text-[#F25F22]" : "text-slate-500"}`} />
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                      >
                        <p
                          data-testid={`faq-answer-${i}`}
                          className="px-6 pb-6 text-sm leading-relaxed text-slate-400"
                        >
                          {f.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
